<template>
  <div class="nl-page">
    <!-- header rail — the eyebrow + title + actions band the newer pages
         use (erm/support detail rails), on neutral structural tokens
         rather than the ops steel so it stays domain-correct. -->
    <div class="nl-rail">
      <div class="nl-rail-text">
        <div class="nl-rail-eyebrow">Notification Center</div>
        <h2 class="nl-rail-title">Notifications</h2>
        <div class="nl-rail-sub">
          <template v-if="loading">Loading...</template>
          <template v-else-if="!items.length">No notifications yet</template>
          <template v-else-if="unreadCount > 0">
            {{ unreadCount }} unread &middot; {{ items.length }} total
          </template>
          <template v-else>{{ items.length }} total &middot; all caught up</template>
        </div>
      </div>
      <div class="nl-rail-actions">
        <b-button
          v-if="unreadCount > 0"
          size="sm"
          variant="outline-secondary"
          class="nl-mark-all-btn"
          @click="handleMarkAllRead"
        >
          <i class="fa fa-check-circle-o"></i> Mark all read
        </b-button>
      </div>
    </div>

    <!-- filter pills — per-category colored chips (the erm/ops-desk filter
         chip language): each chip carries its notification type's hue as a
         colored outline when idle and a solid fill when active. hues match
         this page's row pucks/type-chips so chips and rows read as one system.
         "all" stays the neutral reset; "unread" is a state, so it takes a
         plain blue. -->
    <div class="nl-filters" role="tablist" aria-label="Filter notifications">
      <button
        v-for="f in filters"
        :key="f.value"
        type="button"
        class="nl-filter-btn"
        :class="[
          f.color ? 'nl-fc-' + f.color : '',
          { active: activeFilter === f.value },
        ]"
        :aria-selected="activeFilter === f.value"
        role="tab"
        @click="activeFilter = f.value"
      >
        <i v-if="f.icon" class="fa" :class="f.icon" aria-hidden="true"></i>
        {{ f.text }}
        <span v-if="f.count != null" class="nl-filter-count">{{ f.count }}</span>
      </button>
    </div>

    <!-- loading: skeleton rows shaped like real items (puck + two lines),
         widths varied so they don't read as identical stripes. -->
    <div v-if="loading" class="nl-list" aria-hidden="true">
      <div v-for="i in 4" :key="'nl-skel-' + i" class="nl-skel-row">
        <span class="nl-skel nl-skel-puck"></span>
        <span class="nl-skel-lines">
          <span class="nl-skel nl-skel-line" :style="{ width: (i % 2 ? 62 : 45) + '%' }"></span>
          <span class="nl-skel nl-skel-line nl-skel-line--sub"></span>
        </span>
      </div>
    </div>
    <div v-else-if="!filteredItems.length" class="nl-state nl-state-empty">
      <i class="fa fa-bell-o" aria-hidden="true"></i>
      <div class="nl-state-title">No notifications</div>
      <div class="nl-state-sub">
        <template v-if="activeFilter === 'unread'">You're all caught up.</template>
        <template v-else-if="activeFilter === 'service_alerts'">No service alerts right now — everything's running normally.</template>
        <template v-else-if="activeFilter === 'announcements'">No announcements yet.</template>
        <template v-else-if="activeFilter === 'reports'">No report-ready notifications yet.</template>
        <template v-else-if="activeFilter === 'support_comments'">No support comments yet.</template>
        <template v-else>Notifications will show up here as they arrive.</template>
      </div>
    </div>
    <div v-else class="nl-list">
      <section v-for="g in groupedItems" :key="g.key" class="nl-group">
        <div class="nl-group-header">{{ g.label }}</div>
        <a v-for="n in g.items"
           :key="n.notification_id"
           :href="n.link || '#'"
           class="nl-item"
           :class="{ 'nl-item-unread': !n.read_at, ['nl-item-' + typeStyle(n.type)]: true }"
           @click.stop.prevent="handleItemClick(n)">
          <!-- type puck: same gradient family as the profile-popup cards
               so the two surfaces read as the same system, intentionally
               smaller (28px vs popup's 34px) since we're already inside
               a dedicated notifications view and don't need to shout. -->
          <span class="nl-item-puck">
            <!-- report_complete gets a custom stroked document + checkmark
                 glyph; other types stay on FA icons. the outlined doc
                 pairs better with the inverted puck's "outline + colored
                 interior" language than FA's filled check-circle. -->
            <svg v-if="n.type === 'report_complete'" class="nl-puck-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <path d="M14 2v6h6"/>
              <polyline points="8 14 11 17 16 12"/>
            </svg>
            <template v-else>
              <span v-if="_isSvgIconWrap(iconForRow(n))" class="ni-svg" v-html="_svgIconHtmlWrap(iconForRow(n))" aria-hidden="true"></span>
              <i v-else class="fa" :class="iconForRow(n)" aria-hidden="true"></i>
            </template>
          </span>
          <div class="nl-item-body">
            <div class="nl-item-row-top">
              <span class="nl-item-heading">{{ n.title }}</span>
              <span class="nl-item-time">{{ absoluteTime(n.created_at) }}</span>
            </div>
            <!-- body_preview is rendered as html so support_comment cards
                 can bold the commenter's name (FI-1642). all server-side
                 paths that build body_preview html-escape user content
                 before composing the string. other types' body_previews
                 are plain text so they render the same as before.
                 cleanBodyPreview() strips half-tag fragments left over
                 from rows fanned out before _strip_markdown was fixed
                 (e.g. `<iLabyrinth</i pit rock hands`) while leaving
                 well-formed support_comment markup alone. -->
            <div v-if="n.body_preview" class="nl-item-preview" v-html="cleanBodyPreview(n.body_preview)"></div>
            <div class="nl-item-meta">
              <span class="nl-type-chip" :class="'nl-type-chip-' + typeStyle(n.type)">
                {{ typeLabel(n.type) }}
              </span>
            </div>
          </div>
        </a>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { parseSupportTicketLink, openSupportTicket } from '@/utils/support_ticket_links'
import {
  get_user_notifications as cm_get_user_notifications,
  mark_all_notifications_read as cm_mark_all_notifications_read,
  record_notification_action as cm_record_notification_action,
} from '../networking/apis'
import {
  tbn_get_user_notifications,
  tbn_mark_all_notifications_read,
  tbn_record_notification_action,
} from '../networking/tbn_apis'
import { iconForNotification, isSvgIcon, customSvgHtml } from '../utils/notification_icons'

export default {
  name: 'NotificationsList',
  data() {
    return {
      loading: true,
      items: [],
      activeFilter: 'all',
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      storeUnread: state => state.notifications.unreadCount,
      storeItems: state => state.notifications.notifications,
    }),
    isTbn() {
      const tenant = this.user && this.user.tenant
      const types = this.user && this.user.types
      return !!(tenant && types && types[tenant] === 'TBN')
    },
    unreadCount() {
      return this.items.filter(n => !n.read_at).length
    },
    // chip icons mirror the announcements admin list where the filter
    // exists on both surfaces (alerts, announcements) so the two pages
    // read as one system; the rest reuse the notification-type glyphs.
    filters() {
      const all = this.items.length
      const unread = this.items.filter(n => !n.read_at).length
      const serviceAlerts = this.items.filter(n => n.type === 'service_alert').length
      const announcements = this.items.filter(n => n.type === 'announcement').length
      const reports = this.items.filter(n => n.type === 'report_complete').length
      const supportComments = this.items.filter(n => n.type === 'support_comment').length
      return [
        { value: 'all', text: 'All', count: all },
        { value: 'unread', text: 'Unread', icon: 'fa-circle', count: unread, color: 'blue' },
        { value: 'service_alerts', text: 'Service alerts', icon: 'fa-exclamation-triangle', count: serviceAlerts, color: 'amber' },
        { value: 'announcements', text: 'Announcements', icon: 'fa-bullhorn', count: announcements, color: 'purple' },
        { value: 'reports', text: 'Reports', icon: 'fa-file-text-o', count: reports, color: 'green' },
        { value: 'support_comments', text: 'Support comments', icon: 'fa-comments-o', count: supportComments, color: 'sky' },
      ]
    },
    filteredItems() {
      if (this.activeFilter === 'unread') return this.items.filter(n => !n.read_at)
      if (this.activeFilter === 'service_alerts') return this.items.filter(n => n.type === 'service_alert')
      if (this.activeFilter === 'announcements') return this.items.filter(n => n.type === 'announcement')
      if (this.activeFilter === 'reports') return this.items.filter(n => n.type === 'report_complete')
      if (this.activeFilter === 'support_comments') return this.items.filter(n => n.type === 'support_comment')
      return this.items
    },
    // group the filtered list by relative day bucket for easy scanning.
    // buckets mirror the conventions in Gmail/Slack lists: Today,
    // Yesterday, This week, Earlier.
    groupedItems() {
      const startOfToday = moment().startOf('day')
      const startOfYesterday = moment().subtract(1, 'day').startOf('day')
      const startOfWeek = moment().startOf('week')
      const buckets = { today: [], yesterday: [], week: [], older: [] }
      for (const n of this.filteredItems) {
        const d = moment(n.created_at)
        if (!d.isValid()) { buckets.older.push(n); continue }
        if (d.isSameOrAfter(startOfToday)) buckets.today.push(n)
        else if (d.isSameOrAfter(startOfYesterday)) buckets.yesterday.push(n)
        else if (d.isSameOrAfter(startOfWeek)) buckets.week.push(n)
        else buckets.older.push(n)
      }
      const groups = []
      if (buckets.today.length) groups.push({ key: 'today', label: 'Today', items: buckets.today })
      if (buckets.yesterday.length) groups.push({ key: 'yesterday', label: 'Yesterday', items: buckets.yesterday })
      if (buckets.week.length) groups.push({ key: 'week', label: 'Earlier this week', items: buckets.week })
      if (buckets.older.length) groups.push({ key: 'older', label: 'Older', items: buckets.older })
      return groups
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const fn = this.isTbn ? tbn_get_user_notifications : cm_get_user_notifications
        const resp = await fn(this.user.tenant, 100)
        const axiosData = (resp && resp.value && resp.value.data) || {}
        const payload = axiosData.data || axiosData
        this.items = (payload && payload.items) || []
      } catch (_) {
        this.items = []
      } finally {
        this.loading = false
      }
    },
    // delegates to the shared notification-icon registry — prefers the
    // per-row icon override (set by the announcement composer) and falls
    // back to the type default. report_complete still uses the inline
    // stroked SVG above this template branch, so we don't need a special
    // case here.
    iconForRow(n) {
      return iconForNotification(n)
    },
    // strip half-tag fragments (e.g. `<iLabyrinth</i`) left over from
    // rows fanned out before _strip_markdown was fixed to handle
    // embedded html. the regex only matches `<…` or `</…` that has NO
    // closing `>` before the next `<` — well-formed tags pass through
    // so support_comment's `<b>Name: </b>` bolding still works.
    cleanBodyPreview(raw) {
      if (!raw) return ''
      return String(raw).replace(/<\/?[a-zA-Z]\w*(?![^<]*>)/g, '')
    },
    _isSvgIconWrap(name) {
      return isSvgIcon(name)
    },
    _svgIconHtmlWrap(name) {
      return customSvgHtml(name)
    },
    typeStyle(type) {
      if (type === 'announcement') return 'announcement'
      if (type === 'report_complete') return 'report'
      if (type === 'service_alert') return 'service-alert'
      if (type === 'service_resolved') return 'service-resolved'
      if (type === 'support_comment') return 'support'
      return 'generic'
    },
    typeLabel(type) {
      if (type === 'announcement') return 'Announcement'
      if (type === 'report_complete') return 'Report ready'
      if (type === 'service_alert') return 'Service alert'
      if (type === 'service_resolved') return 'Service resolved'
      if (type === 'support_comment') return 'Support comment'
      return 'Notification'
    },
    absoluteTime(iso) {
      if (!iso) return ''
      try {
        const m = moment(iso)
        const diffH = Math.abs(moment().diff(m, 'hours'))
        if (diffH < 24) return m.format('h:mm A')
        return m.format('MMM D, h:mm A')
      } catch (_) { return '' }
    },
    async handleItemClick(n) {
      if (!n || !n.notification_id) return
      this.$store.commit('notifications/apply_action_local', n.notification_id)
      const target = this.items.find(x => x.notification_id === n.notification_id)
      if (target && !target.read_at) {
        const now = new Date().toISOString()
        target.read_at = now
        target.actioned_at = now
      }
      try {
        const fn = this.isTbn ? tbn_record_notification_action : cm_record_notification_action
        await fn(this.user.tenant, n.notification_id)
      } catch (_) { /* best-effort */ }
      // defensive: '/' is the router's login redirect and an empty/whitespace
      // link has no destination. either would boot the user to the login
      // page — skip navigation instead of kicking them out of the app.
      const dest = (n.link || '').trim()
      // a support ticket opens in place, over the notifications page
      const ticket = parseSupportTicketLink(dest)
      if (ticket) {
        openSupportTicket(this, ticket)
        return
      }
      if (dest && dest !== '/') {
        if (dest.startsWith('http')) window.location.href = dest
        else this.$router.push(dest).catch(() => {})
      }
    },
    async handleMarkAllRead() {
      const now = new Date().toISOString()
      this.items.forEach(n => { if (!n.read_at) n.read_at = now })
      this.$store.commit('notifications/mark_all_read_local')
      try {
        const fn = this.isTbn ? tbn_mark_all_notifications_read : cm_mark_all_notifications_read
        await fn(this.user.tenant)
      } catch (_) { /* best-effort */ }
    },
  },
}
</script>

<style scoped>
/* flat page — no floating card, no gradient surface. the list sits
   directly on the page background so it reads like the other listing
   pages, while colored type pucks + type chips carry the personality
   that makes notifications their own thing. */
.nl-page {
  max-width: 860px;
  margin: 20px auto 40px;
  padding: 0 16px;
  text-align: left;
}

/* header rail — the erm/support rail shape (accent pipe + eyebrow +
   title + right-side actions) on neutral structural tokens: sunken band,
   rail-accent pipe, square left edge / soft right. */
.nl-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--fi-surface-sunken);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}

.nl-rail-text { min-width: 0; flex: 1; }

.nl-rail-eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--fi-text-muted);
  margin-bottom: 1px;
}

.nl-rail-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--fi-text);
  margin: 0;
  line-height: 1.25;
}

.nl-rail-sub {
  margin-top: 2px;
  font-size: 12px;
  color: var(--fi-text-muted);
}

.nl-rail-actions { flex: 0 0 auto; display: flex; gap: 8px; }

.nl-mark-all-btn { flex: 0 0 auto; }

.nl-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}

/* filter pills — the announcements-list chip spec verbatim (999px pill,
   icon + label + tabular count, azure action fill when active) so the
   user and admin notification surfaces share one filter language. */
.nl-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  color: var(--fi-text-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}

.nl-filter-btn:hover {
  background: #f6f9fc;
  border-color: #b8c9da;
  color: var(--fi-text);
}

.nl-filter-btn.active {
  background: var(--fi-action-primary);
  border-color: var(--fi-action-primary);
  color: #fff;
}

.nl-filter-btn.active:hover {
  background: var(--fi-brand-strong);
  border-color: var(--fi-brand-strong);
}

.nl-filter-btn .fa { font-size: 11px; }

/* the unread chip's dot glyph reads better a step smaller */
.nl-filter-btn .fa-circle { font-size: 7px; }

.nl-filter-count {
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
  padding: 1px 6px;
  border-radius: 999px;
  margin-left: 2px;
  min-width: 18px;
  text-align: center;
}

.nl-filter-btn.active .nl-filter-count {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* per-category colored chips (erm/ops-desk chip language): each type's hue
   as a 1.5px colored outline idle + a solid same-hue fill when active. hues
   match this page's row pucks/type-chips so chips and rows read as one system.
   equal specificity to the base .nl-filter-btn rules above, so these stay
   after them to win the source-order tie. */
.nl-filter-btn[class*="nl-fc-"] { border-width: 1.5px; }
.nl-fc-blue   { border-color: #3f79b0; color: #2f5c86; }
.nl-fc-amber  { border-color: #d97706; color: #8a5a00; }
.nl-fc-purple { border-color: #7c5dd0; color: #4b2c92; }
.nl-fc-green  { border-color: #10b981; color: #157347; }
.nl-fc-sky    { border-color: #0284c7; color: #075985; }
.nl-filter-btn[class*="nl-fc-"]:not(.active):hover { background: #f4f7fa; }
.nl-fc-blue.active   { background: #3f79b0; border-color: #3f79b0; color: #fff; }
.nl-fc-amber.active  { background: #d97706; border-color: #d97706; color: #15202b; }
.nl-fc-purple.active { background: #7c5dd0; border-color: #7c5dd0; color: #fff; }
.nl-fc-green.active  { background: #10b981; border-color: #10b981; color: #15202b; }
.nl-fc-sky.active    { background: #0284c7; border-color: #0284c7; color: #fff; }
/* amber/green active fills are light, so the base white count badge would
   vanish on them -- give those a dark-tinted badge instead. */
.nl-fc-amber.active .nl-filter-count,
.nl-fc-green.active .nl-filter-count { background: rgba(0, 0, 0, 0.16); color: inherit; }

/* loading skeletons — item-shaped placeholder rows with the house
   shimmer (support/erm list precedent). */
.nl-skel-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-top: 1px solid var(--fi-line);
}

.nl-skel {
  display: block;
  background: linear-gradient(90deg, #eceff3 0%, #e2e7ee 50%, #eceff3 100%);
  background-size: 200% 100%;
  animation: nl-skel-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.nl-skel-puck { flex: 0 0 auto; width: 28px; height: 28px; border-radius: 8px; }

.nl-skel-lines { flex: 1; min-width: 0; }

.nl-skel-line { height: 12px; margin-top: 3px; }

.nl-skel-line--sub { height: 10px; width: 84%; margin-top: 8px; }

@keyframes nl-skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .nl-skel { animation: none; }
}

.nl-state {
  text-align: center;
  padding: 56px 20px;
  color: var(--fi-text-muted);
}

.nl-state-empty .fa {
  display: block;
  font-size: 36px;
  margin-bottom: 10px;
  color: var(--fi-line-strong);
}

.nl-state-title { font-size: 15px; font-weight: 600; color: var(--fi-text); margin-bottom: 4px; }
.nl-state-sub { font-size: 13px; }

.nl-group + .nl-group { margin-top: 16px; }

.nl-group-header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: var(--fi-text-faint);
  padding: 0 2px;
  margin: 2px 0 6px;
}

/* rows — thin top border like table rows, flat backgrounds. read state
   carried implicitly: unread rows get a steelblue leading accent bar +
   a subtle blue-tinted background, mirroring the gold/cream admin-row
   treatment used elsewhere in the app. read rows are plain white. no
   redundant "Read/Unread/Actioned" text — the user knows they read
   their own notifications. */
.nl-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 14px 10px 14px;
  border-top: 1px solid var(--fi-line);
  background: var(--fi-surface);
  color: var(--fi-text);
  text-decoration: none;
  transition: background 0.12s;
}

.nl-item:last-child { border-bottom: 1px solid var(--fi-line); }

.nl-item:hover,
.nl-item:focus { background: #f6f9fc; color: var(--fi-text); text-decoration: none; }

/* unread = leading accent rail only (inset box-shadow so no layout
   shift vs read rows). dropped the #f4f8ff tint 2026-04-29 to match
   the navbar popup — the rail + read-state puck softening already
   carry the unread signal cleanly. hover bg falls through to the
   default `.nl-item:hover` (#f6f9fc) so unread + read rows hover
   identically. */
.nl-item-unread {
  box-shadow: inset 4px 0 0 0 var(--fi-rail-accent);
  padding-left: 18px;
}

/* puck — inverted outline style (pastel fill + colored border + colored
   icon) matching the profile-popup notification cards. intentionally
   smaller than the popup's 34px (this is a dedicated page, not a
   condensed hover surface, so the icon doesn't need to be the visual
   anchor). same palette + read/unread border behavior keeps the two
   surfaces reading as the same system. */
.nl-item-puck {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-top: 1px;
  box-sizing: border-box;
  background: #f1f5f9;
  border: 1.5px solid #94a3b8;
  color: #64748b;
}

.nl-puck-svg {
  width: 16px;
  height: 16px;
}

.nl-item-report .nl-item-puck {
  background: #ecfdf5;
  border-color: #10b981;
  color: #10b981;
}
.nl-item-announcement .nl-item-puck {
  background: #f3f0ff;
  border-color: #7c5dd0;
  color: #7c5dd0;
}
/* service alerts: amber to convey urgency without alarm-red. matches
   the amber used for admin-row affordances elsewhere so "this deserves
   your attention" reads consistently across the app. */
.nl-item-service-alert .nl-item-puck {
  background: #fef3c7;
  border-color: #d97706;
  color: #d97706;
}
/* service_resolved — green "all clear". distinct from report-complete
   green by using #059669 (the slightly deeper emerald) for border + icon
   so a glance distinguishes "we're back online" from "your report is
   ready". */
.nl-item-service-resolved .nl-item-puck {
  background: #ecfdf5;
  border-color: #059669;
  color: #059669;
}
/* support comments: sky-blue. distinct from the steelblue accent rail
   on unread cards but in the same blue family so the affordance reads
   as "communication" rather than "alert" or "broadcast". */
.nl-item-support .nl-item-puck {
  background: #e0f2fe;
  border-color: #0284c7;
  color: #0284c7;
}

/* read state: softer pastel border so read items visually step back
   from unread ones. bg + icon color stay fully saturated, the type
   is still identifiable at a glance, only the frame dims. */
.nl-item-report:not(.nl-item-unread) .nl-item-puck { border-color: #a7f3d0; }
.nl-item-announcement:not(.nl-item-unread) .nl-item-puck { border-color: #d8cffb; }
.nl-item-service-alert:not(.nl-item-unread) .nl-item-puck { border-color: #fde68a; }
.nl-item-service-resolved:not(.nl-item-unread) .nl-item-puck { border-color: #a7f3d0; }
.nl-item-support:not(.nl-item-unread) .nl-item-puck { border-color: #bae6fd; }
.nl-item-generic:not(.nl-item-unread) .nl-item-puck { border-color: #cbd5e1; }

.nl-item-body { flex: 1; min-width: 0; }

.nl-item-row-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.nl-item-heading {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--fi-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nl-item-time {
  flex: 0 0 auto;
  font-size: 11px;
  color: var(--fi-text-faint);
  font-variant-numeric: tabular-nums;
}

.nl-item-preview {
  font-size: 13px;
  color: var(--fi-text-muted);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  margin-top: 2px;
}

.nl-item-meta {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

/* type chip — colored pill that tags what kind of notification this is.
   the color pairs with the puck gradient so a scan down the list reads
   visually consistent (purple puck ↔ purple ANNOUNCEMENT chip, green
   puck ↔ green REPORT READY chip). */
.nl-type-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: #e9ecef;
  color: #495057;
}

.nl-type-chip-announcement { background: #f1ebff; color: #4b2c92; }
.nl-type-chip-report { background: #e9f6ee; color: #155724; }
.nl-type-chip-service-alert { background: #fef3d5; color: #8a5a00; }
.nl-type-chip-service-resolved { background: #d1fae5; color: #065f46; }
.nl-type-chip-support { background: #e0f2fe; color: #075985; }

@media (max-width: 600px) {
  .nl-page { margin-top: 12px; }
  .nl-item { padding-left: 18px; padding-right: 10px; }
}
</style>
